import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import { getCdnBaseUrl, getCurrentThemeFolder, getImageUrl } from '../utils/cdnUtils'
import {
  applyPromotion,
  getPromotionDashboard,
} from '../services/promotionService'
import {
  getBirthdayDisplayPromotionConfig,
  getCashbackDisplayPromotionConfig,
  getFacebookShareDisplayPromotionConfig,
  getMonthlyRewardPromotionConfig,
  getPromotionHubDisplayPromotionConfig,
  getReferralDisplayPromotionConfig,
  type UserBirthdayDisplayPromotionConfig,
  type UserCashbackDisplayPromotionConfig,
  type UserCashbackDisplaySettings,
  type UserFacebookShareDisplayPromotionConfig,
  type UserManualReviewBonusDisplaySettings,
  type UserMonthlyRewardDisplaySettings,
  type UserMonthlyRewardPromotionConfig,
  type UserPromotionHubDisplayPromotionConfig,
  type UserPromotionHubDisplaySettings,
  type UserPromotionHubManualCard,
  type UserPromotionHubShortcutSettings,
  type UserReferralDisplayPromotionConfig,
  type UserReferralDisplaySettings,
} from '../services/promotionConfigService'

type PromotionCard = {
  id: string
  title: string
  description: string
  buttonText: string
  campaignId?: string
  imageUrl?: string
  actionType?: 'campaign' | 'route' | 'url' | 'none'
  actionValue?: string
  openInNewTab?: boolean
  sortOrder?: number
}

const DEFAULT_IMAGE = getImageUrl('logo-m.png')

const DEFAULT_CASHBACK_DISPLAY_SETTINGS: UserCashbackDisplaySettings = {
  pageTitle: 'เงินคืนยอดเล่น',
  pageSubtitle: 'ตรวจสอบยอดคืนล่าสุด กดรับ cashback ได้ทันที และดูเงื่อนไขได้ในหน้าจอเดียว',
  heroBadgeText: 'Cashback Center',
  withdrawalButtonText: 'ถอนเงินแนะนำ',
  noticeText: 'รายได้การคืนยอดเล่นของท่านสามารถแจ้งถอนได้ทุกเวลา หากมียอดรายได้มากกว่า 15 บาทขึ้นไป',
  minWithdrawalAmount: 15,
  cashbackRateText: '0.3% - 0.8%',
  promotionalMessage: 'สุดยอดโปรโมชั่น รับเงินคืนฟรี ทันทีทุกวัน ทั้งวัน ไม่อั้น ไม่ติดเทิร์น ง่ายๆ',
  disabledTitle: 'ปิดใช้งานชั่วคราว',
  disabledMessage: 'ระบบ cashback ปิดใช้งานในขณะนี้ กรุณาติดต่อแอดมินหากมีคำถาม',
  historyEmptyText: 'ยังไม่มีประวัติ cashback',
  showRateTable: true,
  showExample: true,
  promotionRoute: '/cashback',
  promotionCard: {
    imageUrl: '',
    imageAlt: 'คืนยอดเสีย',
    badgeText: 'Cashback',
    title: 'คืนยอดเสีย',
    description: 'ดูยอด cashback ล่าสุด กดรับสิทธิ์ และดูประวัติการรับเงินคืน',
    buttonText: 'ไปหน้า Cashback',
  },
}

const DEFAULT_REFERRAL_DISPLAY_SETTINGS: UserReferralDisplaySettings = {
  pageTitle: 'แนะนำเพื่อน',
  pageSubtitle: 'แชร์ลิงก์ของคุณเพื่อรับค่าคอมมิชชั่นจากยอดเล่นของสมาชิกที่คุณแนะนำ',
  withdrawButtonText: 'ถอนคอมมิชชั่น',
  minCommissionWithdrawalAmount: 100,
  shareSectionTitle: 'ลิงก์แนะนำ',
  linkLabel: 'ลิงก์สำหรับโปรโมท',
  copyButtonText: 'คัดลอก',
  shareDescription: '',
  disabledTitle: 'ปิดใช้งานชั่วคราว',
  disabledMessage: 'ระบบแนะนำเพื่อนปิดใช้งานในขณะนี้ กรุณาติดต่อแอดมินหากมีคำถาม',
  historyEmptyText: 'ยังไม่พบรายการ',
  hideZeroCommissionSegments: true,
  promotionRoute: '/affiliate',
  promotionCard: {
    imageUrl: '',
    imageAlt: 'แนะนำเพื่อน',
    badgeText: 'Referral',
    title: 'แนะนำเพื่อน',
    description: 'ดูคอมมิชชั่น ลิงก์โปรโมท และประวัติการแนะนำเพื่อน',
    buttonText: 'ไปหน้าแนะนำเพื่อน',
  },
}

const DEFAULT_BIRTHDAY_DISPLAY_SETTINGS: UserManualReviewBonusDisplaySettings = {
  pageTitle: 'โบนัสวันเกิด',
  pageSubtitle: 'ส่งหลักฐานยืนยันวันเกิดให้แอดมินตรวจสอบก่อนอนุมัติรับสิทธิ์โบนัส',
  contactSectionTitle: 'เอกสารที่ต้องส่งให้แอดมิน',
  contactSectionSubtitle: 'กรุณาส่งหลักฐานให้ครบถ้วนเพื่อให้ทีมงานตรวจสอบและอนุมัติได้รวดเร็ว',
  requirements: [
    'ส่งรูปบัตรประชาชนที่เห็นชื่อและวันเดือนปีเกิดชัดเจน',
    'แจ้ง username หรือเบอร์โทรที่ใช้สมัครสมาชิก',
    'รอแอดมินตรวจสอบความถูกต้องก่อนรับโบนัส',
  ],
  disabledTitle: 'ปิดใช้งานชั่วคราว',
  disabledMessage: 'ระบบโบนัสวันเกิดปิดใช้งานในขณะนี้ กรุณาติดต่อแอดมินหากมีคำถาม',
  promotionCard: {
    imageUrl: '',
    imageAlt: 'โบนัสวันเกิด',
    badgeText: 'Birthday',
    title: 'โบนัสวันเกิด',
    description: 'ส่งรูปบัตรประชาชนให้แอดมินตรวจสอบ เพื่อรับสิทธิ์โบนัสวันเกิดตามเงื่อนไขของเว็บไซต์',
    buttonText: 'ติดต่อแอดมิน',
  },
}

const DEFAULT_FACEBOOK_SHARE_DISPLAY_SETTINGS: UserManualReviewBonusDisplaySettings = {
  pageTitle: 'โบนัสแชร์เฟส',
  pageSubtitle: 'ส่งภาพโพสต์และลิงก์ที่แชร์ให้แอดมินตรวจสอบก่อนอนุมัติรับสิทธิ์โบนัส',
  contactSectionTitle: 'หลักฐานที่ต้องส่งให้แอดมิน',
  contactSectionSubtitle: 'กรุณาส่งข้อมูลการแชร์ให้ครบถ้วนเพื่อให้ทีมงานตรวจสอบได้ทันที',
  requirements: [
    'แคปภาพโพสต์ Facebook ที่แชร์แล้วให้เห็นชัดเจน',
    'ส่ง URL ของโพสต์ที่แชร์เพื่อให้แอดมินเปิดตรวจสอบได้',
    'แจ้ง username หรือเบอร์โทรที่ใช้สมัครสมาชิก',
  ],
  disabledTitle: 'ปิดใช้งานชั่วคราว',
  disabledMessage: 'ระบบโบนัสแชร์เฟสปิดใช้งานในขณะนี้ กรุณาติดต่อแอดมินหากมีคำถาม',
  promotionCard: {
    imageUrl: '',
    imageAlt: 'โบนัสแชร์เฟส',
    badgeText: 'Facebook',
    title: 'โบนัสแชร์เฟส',
    description: 'แคปภาพโพสต์และส่ง URL ที่แชร์ให้แอดมินตรวจสอบก่อนอนุมัติรับโบนัส',
    buttonText: 'ติดต่อแอดมิน',
  },
}

const DEFAULT_MONTHLY_REWARD_CONFIG: UserMonthlyRewardPromotionConfig = {
  promotionType: 'monthly_reward',
  enabled: true,
  settings: {
    display: {
      pageTitle: 'รางวัลประจำเดือน',
      subtitle: 'อัปเดตอันดับเดือนปัจจุบันแบบ live และเติมข้อมูลจำลองให้น่าเชื่อถือเมื่อข้อมูลจริงยังไม่ครบ',
      rowsPerTab: 10,
      promotionCard: {
        badgeText: 'Reward',
        title: 'รางวัลประจำเดือน',
        description: 'ติดตามอันดับล่าสุดและสิทธิ์รับรางวัลประจำเดือนของสมาชิก',
        buttonText: 'ไปหน้ารางวัล',
        route: '/reward',
      },
    },
  },
}

const DEFAULT_SHORTCUT = (
  sortOrder: number,
  badgeText: string,
  title: string,
  description: string,
  buttonText: string,
  route: string
): UserPromotionHubShortcutSettings => ({
  enabled: true,
  sortOrder,
  badgeText,
  title,
  description,
  buttonText,
  route,
})

const DEFAULT_PROMOTION_HUB_SETTINGS: UserPromotionHubDisplaySettings = {
  pageTitle: 'โบนัสและโปรโมชั่น',
  pageSubtitle: 'รวมสิทธิพิเศษและโบนัสที่สมาชิกสามารถใช้งานได้',
  heroBadgeText: 'Promotion Center',
  warningTitle: 'คำเตือน',
  warningMessagePrimary: 'ทุกโบนัสต้องกดรับหลังจากฝากเงินเท่านั้น ห้ามเล่นก่อนเด็ดขาด',
  warningMessageSecondary: 'หากทีมงานตรวจสอบว่ามีการรับโบนัสเพื่อแทงสวน ทางทีมงานจะไม่ทำรายการถอนให้ทุกกรณี',
  featuredSectionTitle: 'โปรโมชันแนะนำ',
  featuredSectionSubtitle: 'โปรโมชันเด่นที่ทีมงานคัดไว้ให้ใช้งานได้สะดวกจากหน้าเดียว',
  campaignsSectionTitle: 'โปรโมชั่นที่เปิดใช้งาน',
  campaignsSectionSubtitle: 'กดรับโบนัสได้จากรายการด้านล่าง',
  emptyCampaignText: 'ขณะนี้ยังไม่มีโปรโมชั่นที่เปิดใช้งาน',
  receiveButtonText: 'รับโบนัส',
  showCampaigns: true,
  disabledTitle: 'ปิดใช้งานหน้ารวมโปรโมชั่นชั่วคราว',
  disabledMessage: 'ขณะนี้ระบบปิดหน้าโปรโมชั่น กรุณาติดต่อแอดมินหากต้องการข้อมูลเพิ่มเติม',
  sections: {
    warning: { enabled: true, sortOrder: 1 },
    featuredPromotions: { enabled: true, sortOrder: 2 },
    campaigns: { enabled: true, sortOrder: 3 },
    shortcuts: { enabled: true, sortOrder: 4 },
  },
  manualCards: [],
  shortcuts: {
    cashback: DEFAULT_SHORTCUT(1, 'Cashback', 'รับเครดิตเงินคืน', 'ดูยอด cashback ล่าสุด กดรับสิทธิ์ และติดตามสถานะการรับเงินคืนได้ทันที', 'ไปหน้า Cashback', '/cashback'),
    monthlyReward: DEFAULT_SHORTCUT(2, 'Reward', 'รางวัลประจำเดือน', 'ติดตามอันดับล่าสุดและสิทธิ์รับรางวัลประจำเดือนของสมาชิก', 'ไปหน้ารางวัล', '/reward'),
    referral: DEFAULT_SHORTCUT(3, 'Referral', 'แนะนำเพื่อน', 'ชวนเพื่อนสมัครผ่านลิงก์ของคุณ เพื่อติดตามสมาชิกและรายได้แนะนำเพื่อน', 'ไปหน้าแนะนำเพื่อน', '/affiliate'),
    birthday: DEFAULT_SHORTCUT(4, 'Birthday', 'โบนัสวันเกิด', 'ส่งรูปบัตรประชาชนให้แอดมินตรวจสอบ เพื่อรับสิทธิ์โบนัสวันเกิดตามเงื่อนไขของเว็บไซต์', 'ติดต่อแอดมิน', '/contact?topic=birthday-bonus'),
    facebookShare: DEFAULT_SHORTCUT(5, 'Facebook', 'โบนัสแชร์เฟส', 'แคปภาพโพสต์และส่ง URL ที่แชร์ให้แอดมินตรวจสอบก่อนอนุมัติรับโบนัส', 'ติดต่อแอดมิน', '/contact?topic=facebook-share-bonus'),
  },
}

const DEFAULT_MANUAL_CARD = (sortOrder: number): UserPromotionHubManualCard => ({
  id: `promotion-card-${sortOrder}`,
  enabled: true,
  sortOrder,
  badgeText: '',
  title: '',
  description: '',
  imageUrl: '',
  imageAlt: '',
  buttonText: 'ดูรายละเอียด',
  actionType: 'none',
  actionValue: '',
  openInNewTab: false,
  startsAt: null,
  endsAt: null,
})

function normalizeReferralDisplayConfig(config?: UserReferralDisplayPromotionConfig | null): UserReferralDisplayPromotionConfig {
  const settings = config?.settings || {}
  return {
    promotionType: 'referral_display',
    enabled: config?.enabled !== false,
    settings: {
      ...DEFAULT_REFERRAL_DISPLAY_SETTINGS,
      ...settings,
      promotionCard: {
        ...DEFAULT_REFERRAL_DISPLAY_SETTINGS.promotionCard,
        ...(settings.promotionCard || {}),
      },
    },
    updatedAt: config?.updatedAt ?? null,
    updatedBy: config?.updatedBy ?? null,
  }
}

function normalizeCashbackDisplayConfig(config?: UserCashbackDisplayPromotionConfig | null): UserCashbackDisplayPromotionConfig {
  const settings = config?.settings || {}
  return {
    promotionType: 'cashback_display',
    enabled: config?.enabled !== false,
    settings: {
      ...DEFAULT_CASHBACK_DISPLAY_SETTINGS,
      ...settings,
      promotionCard: {
        ...DEFAULT_CASHBACK_DISPLAY_SETTINGS.promotionCard,
        ...(settings.promotionCard || {}),
      },
    },
    updatedAt: config?.updatedAt ?? null,
    updatedBy: config?.updatedBy ?? null,
  }
}

function normalizeManualReviewBonusDisplayConfig<TConfig extends { enabled: boolean; settings?: Partial<UserManualReviewBonusDisplaySettings> }>(
  config: TConfig | null | undefined,
  defaults: UserManualReviewBonusDisplaySettings
): TConfig {
  const settings = config?.settings || {}
  return {
    ...(config || {}),
    enabled: config?.enabled !== false,
    settings: {
      ...defaults,
      ...settings,
      requirements:
        Array.isArray(settings.requirements) && settings.requirements.length > 0
          ? settings.requirements.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
          : defaults.requirements,
      promotionCard: {
        ...defaults.promotionCard,
        ...(settings.promotionCard || {}),
      },
    },
  } as TConfig
}

function normalizeMonthlyRewardConfig(config?: UserMonthlyRewardPromotionConfig | null): UserMonthlyRewardPromotionConfig {
  const defaultDisplay = DEFAULT_MONTHLY_REWARD_CONFIG.settings?.display as UserMonthlyRewardDisplaySettings
  const display = config?.settings?.display || {}
  return {
    promotionType: 'monthly_reward',
    enabled: config?.enabled !== false,
    settings: {
      display: {
        ...defaultDisplay,
        ...display,
        promotionCard: {
          ...defaultDisplay.promotionCard,
          ...(display.promotionCard || {}),
        },
      },
    },
    updatedAt: config?.updatedAt ?? null,
    updatedBy: config?.updatedBy ?? null,
  }
}

function normalizePromotionHubConfig(config?: UserPromotionHubDisplayPromotionConfig | null): UserPromotionHubDisplayPromotionConfig {
  const settings = config?.settings || {}
  const sectionDefaults = DEFAULT_PROMOTION_HUB_SETTINGS.sections
  const shortcutDefaults = DEFAULT_PROMOTION_HUB_SETTINGS.shortcuts

  const normalizeShortcut = (
    shortcut: Partial<UserPromotionHubShortcutSettings> | undefined,
    fallback: UserPromotionHubShortcutSettings
  ): UserPromotionHubShortcutSettings => ({
    enabled: typeof shortcut?.enabled === 'boolean' ? shortcut.enabled : fallback.enabled,
    sortOrder: typeof shortcut?.sortOrder === 'number' && Number.isFinite(shortcut.sortOrder) ? shortcut.sortOrder : fallback.sortOrder,
    badgeText: typeof shortcut?.badgeText === 'string' && shortcut.badgeText.trim() ? shortcut.badgeText : fallback.badgeText,
    title: typeof shortcut?.title === 'string' && shortcut.title.trim() ? shortcut.title : fallback.title,
    description: typeof shortcut?.description === 'string' && shortcut.description.trim() ? shortcut.description : fallback.description,
    buttonText: typeof shortcut?.buttonText === 'string' && shortcut.buttonText.trim() ? shortcut.buttonText : fallback.buttonText,
    route: typeof shortcut?.route === 'string' && shortcut.route.trim() ? shortcut.route : fallback.route,
  })

  const normalizeSection = (
    section: Partial<{ enabled: boolean; sortOrder: number }> | undefined,
    fallback: { enabled: boolean; sortOrder: number }
  ) => ({
    enabled: typeof section?.enabled === 'boolean' ? section.enabled : fallback.enabled,
    sortOrder: typeof section?.sortOrder === 'number' && Number.isFinite(section.sortOrder) ? section.sortOrder : fallback.sortOrder,
  })

  const manualCards = Array.isArray(settings.manualCards)
    ? settings.manualCards.map((card, index) => ({
      ...DEFAULT_MANUAL_CARD(index + 1),
      ...card,
      sortOrder: typeof card?.sortOrder === 'number' && Number.isFinite(card.sortOrder) ? card.sortOrder : index + 1,
      actionType:
        card?.actionType === 'campaign' || card?.actionType === 'route' || card?.actionType === 'url'
          ? card.actionType
          : 'none',
      startsAt: typeof card?.startsAt === 'string' && card.startsAt.trim() ? card.startsAt : null,
      endsAt: typeof card?.endsAt === 'string' && card.endsAt.trim() ? card.endsAt : null,
    })) as UserPromotionHubManualCard[]
    : []

  return {
    promotionType: 'promotion_hub_display',
    enabled: config?.enabled !== false,
    settings: {
      pageTitle: typeof settings.pageTitle === 'string' && settings.pageTitle.trim() ? settings.pageTitle : DEFAULT_PROMOTION_HUB_SETTINGS.pageTitle,
      pageSubtitle: typeof settings.pageSubtitle === 'string' && settings.pageSubtitle.trim() ? settings.pageSubtitle : DEFAULT_PROMOTION_HUB_SETTINGS.pageSubtitle,
      heroBadgeText: typeof settings.heroBadgeText === 'string' && settings.heroBadgeText.trim() ? settings.heroBadgeText : DEFAULT_PROMOTION_HUB_SETTINGS.heroBadgeText,
      warningTitle: typeof settings.warningTitle === 'string' && settings.warningTitle.trim() ? settings.warningTitle : DEFAULT_PROMOTION_HUB_SETTINGS.warningTitle,
      warningMessagePrimary:
        typeof settings.warningMessagePrimary === 'string' && settings.warningMessagePrimary.trim()
          ? settings.warningMessagePrimary
          : DEFAULT_PROMOTION_HUB_SETTINGS.warningMessagePrimary,
      warningMessageSecondary:
        typeof settings.warningMessageSecondary === 'string' && settings.warningMessageSecondary.trim()
          ? settings.warningMessageSecondary
          : DEFAULT_PROMOTION_HUB_SETTINGS.warningMessageSecondary,
      featuredSectionTitle:
        typeof settings.featuredSectionTitle === 'string' && settings.featuredSectionTitle.trim()
          ? settings.featuredSectionTitle
          : DEFAULT_PROMOTION_HUB_SETTINGS.featuredSectionTitle,
      featuredSectionSubtitle:
        typeof settings.featuredSectionSubtitle === 'string' && settings.featuredSectionSubtitle.trim()
          ? settings.featuredSectionSubtitle
          : DEFAULT_PROMOTION_HUB_SETTINGS.featuredSectionSubtitle,
      campaignsSectionTitle:
        typeof settings.campaignsSectionTitle === 'string' && settings.campaignsSectionTitle.trim()
          ? settings.campaignsSectionTitle
          : DEFAULT_PROMOTION_HUB_SETTINGS.campaignsSectionTitle,
      campaignsSectionSubtitle:
        typeof settings.campaignsSectionSubtitle === 'string' && settings.campaignsSectionSubtitle.trim()
          ? settings.campaignsSectionSubtitle
          : DEFAULT_PROMOTION_HUB_SETTINGS.campaignsSectionSubtitle,
      emptyCampaignText:
        typeof settings.emptyCampaignText === 'string' && settings.emptyCampaignText.trim()
          ? settings.emptyCampaignText
          : DEFAULT_PROMOTION_HUB_SETTINGS.emptyCampaignText,
      receiveButtonText:
        typeof settings.receiveButtonText === 'string' && settings.receiveButtonText.trim()
          ? settings.receiveButtonText
          : DEFAULT_PROMOTION_HUB_SETTINGS.receiveButtonText,
      showCampaigns: typeof settings.showCampaigns === 'boolean' ? settings.showCampaigns : DEFAULT_PROMOTION_HUB_SETTINGS.showCampaigns,
      disabledTitle:
        typeof settings.disabledTitle === 'string' && settings.disabledTitle.trim()
          ? settings.disabledTitle
          : DEFAULT_PROMOTION_HUB_SETTINGS.disabledTitle,
      disabledMessage:
        typeof settings.disabledMessage === 'string' && settings.disabledMessage.trim()
          ? settings.disabledMessage
          : DEFAULT_PROMOTION_HUB_SETTINGS.disabledMessage,
      sections: {
        warning: normalizeSection(settings.sections?.warning, sectionDefaults.warning),
        featuredPromotions: normalizeSection(settings.sections?.featuredPromotions, sectionDefaults.featuredPromotions),
        shortcuts: normalizeSection(settings.sections?.shortcuts, sectionDefaults.shortcuts),
        campaigns: normalizeSection(settings.sections?.campaigns, sectionDefaults.campaigns),
      },
      manualCards,
      shortcuts: {
        cashback: normalizeShortcut(settings.shortcuts?.cashback, shortcutDefaults.cashback),
        monthlyReward: normalizeShortcut(settings.shortcuts?.monthlyReward, shortcutDefaults.monthlyReward),
        referral: normalizeShortcut(settings.shortcuts?.referral, shortcutDefaults.referral),
        birthday: normalizeShortcut(settings.shortcuts?.birthday, shortcutDefaults.birthday),
        facebookShare: normalizeShortcut(settings.shortcuts?.facebookShare, shortcutDefaults.facebookShare),
      },
    },
    updatedAt: config?.updatedAt ?? null,
    updatedBy: config?.updatedBy ?? null,
  }
}

function isManualCardActive(card: UserPromotionHubManualCard): boolean {
  if (!card.enabled) return false
  const now = Date.now()
  const startsAt = card.startsAt ? new Date(card.startsAt).getTime() : null
  const endsAt = card.endsAt ? new Date(card.endsAt).getTime() : null
  if (startsAt && !Number.isNaN(startsAt) && now < startsAt) return false
  if (endsAt && !Number.isNaN(endsAt) && now > endsAt) return false
  return true
}

const PromotionPage: React.FC = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [applyingId, setApplyingId] = useState<string | null>(null)
  const [campaigns, setCampaigns] = useState<PromotionCard[]>([])
  const [hubConfig, setHubConfig] = useState<UserPromotionHubDisplayPromotionConfig>(() => normalizePromotionHubConfig(null))
  const [cashbackConfig, setCashbackConfig] = useState<UserCashbackDisplayPromotionConfig>(() => normalizeCashbackDisplayConfig(null))
  const [referralConfig, setReferralConfig] = useState<UserReferralDisplayPromotionConfig>(() => normalizeReferralDisplayConfig(null))
  const [birthdayConfig, setBirthdayConfig] = useState<UserBirthdayDisplayPromotionConfig>(() =>
    normalizeManualReviewBonusDisplayConfig<UserBirthdayDisplayPromotionConfig>({ promotionType: 'birthday_display', enabled: true }, DEFAULT_BIRTHDAY_DISPLAY_SETTINGS)
  )
  const [facebookShareConfig, setFacebookShareConfig] = useState<UserFacebookShareDisplayPromotionConfig>(() =>
    normalizeManualReviewBonusDisplayConfig<UserFacebookShareDisplayPromotionConfig>({ promotionType: 'facebook_share_display', enabled: true }, DEFAULT_FACEBOOK_SHARE_DISPLAY_SETTINGS)
  )
  const [monthlyRewardConfig, setMonthlyRewardConfig] = useState<UserMonthlyRewardPromotionConfig>(() => normalizeMonthlyRewardConfig(DEFAULT_MONTHLY_REWARD_CONFIG))

  useEffect(() => {
    let active = true

    void Promise.all([
      getPromotionDashboard(),
      getPromotionHubDisplayPromotionConfig(),
      getCashbackDisplayPromotionConfig(),
      getReferralDisplayPromotionConfig(),
      getBirthdayDisplayPromotionConfig(),
      getFacebookShareDisplayPromotionConfig(),
      getMonthlyRewardPromotionConfig(),
    ])
      .then(([items, hubRes, cashbackRes, referralRes, birthdayRes, facebookShareRes, monthlyRewardRes]) => {
        if (!active) return
        const managedCampaignTypes = new Set(['cashback', 'referral', 'monthly_reward', 'birthday', 'facebook_share'])
        const cards = (items || [])
          .filter((campaign) => !managedCampaignTypes.has(String(campaign.type || '').trim().toLowerCase()))
          .map((campaign, index): PromotionCard => ({
          id: campaign.campaignId || campaign._id || `promotion-${index}`,
          title: campaign.display?.title || campaign.name || 'โปรโมชั่น',
          description: campaign.display?.description || campaign.description || 'โปรโมชั่นพิเศษสำหรับสมาชิก',
          buttonText: campaign.display?.buttonText || 'รับโบนัส',
          campaignId: campaign.campaignId || campaign._id || '',
          imageUrl: campaign.display?.imageUrl || campaign.display?.bannerUrl || campaign.display?.iconUrl || '',
          actionType: 'campaign',
          sortOrder: Number(campaign.display?.displayOrder ?? Number.MAX_SAFE_INTEGER),
        }))

        setCampaigns(cards)
        setHubConfig(normalizePromotionHubConfig(hubRes.success ? hubRes.data : null))
        setCashbackConfig(normalizeCashbackDisplayConfig(cashbackRes.success ? cashbackRes.data : null))
        setReferralConfig(normalizeReferralDisplayConfig(referralRes.success ? referralRes.data : null))
        setBirthdayConfig(
          normalizeManualReviewBonusDisplayConfig<UserBirthdayDisplayPromotionConfig>(
            birthdayRes.success ? birthdayRes.data : { promotionType: 'birthday_display', enabled: true },
            DEFAULT_BIRTHDAY_DISPLAY_SETTINGS
          )
        )
        setFacebookShareConfig(
          normalizeManualReviewBonusDisplayConfig<UserFacebookShareDisplayPromotionConfig>(
            facebookShareRes.success ? facebookShareRes.data : { promotionType: 'facebook_share_display', enabled: true },
            DEFAULT_FACEBOOK_SHARE_DISPLAY_SETTINGS
          )
        )
        setMonthlyRewardConfig(normalizeMonthlyRewardConfig(monthlyRewardRes.success ? monthlyRewardRes.data : DEFAULT_MONTHLY_REWARD_CONFIG))
      })
      .catch(() => {
        if (!active) return
        setCampaigns([])
        setHubConfig(normalizePromotionHubConfig(null))
        setCashbackConfig(normalizeCashbackDisplayConfig(null))
        setReferralConfig(normalizeReferralDisplayConfig(null))
      })
      .finally(() => {
        if (active) setLoading(false)
      })

    return () => {
      active = false
    }
  }, [])

  const resolveImage = (card: PromotionCard) => {
    const themeFolder = getCurrentThemeFolder()
    if (!card.imageUrl) return DEFAULT_IMAGE
    const normalizedPath = String(card.imageUrl).trim()
    if (!normalizedPath) return DEFAULT_IMAGE
    if (normalizedPath.startsWith('http://') || normalizedPath.startsWith('https://')) return normalizedPath
    if (normalizedPath.startsWith('/')) return `${getCdnBaseUrl()}/${themeFolder}${normalizedPath}`
    if (normalizedPath.startsWith('HuayAsia/') || normalizedPath.startsWith('Lenhuay/')) {
      return `${getCdnBaseUrl()}/${normalizedPath}`
    }
    if (normalizedPath.startsWith('img/') || normalizedPath.startsWith('assets/')) {
      return `${getCdnBaseUrl()}/${themeFolder}/${normalizedPath}`
    }
    if (normalizedPath.includes('/')) return `${getCdnBaseUrl()}/${normalizedPath.replace(/^\/+/, '')}`
    return getImageUrl(normalizedPath)
  }

  const handleApply = async (card: PromotionCard) => {
    if (!card.campaignId) {
      await Swal.fire({
        icon: 'info',
        title: 'โปรโมชั่นนี้ยังไม่พร้อม',
        text: 'กรุณาติดต่อแอดมินเพื่อดูรายละเอียดเพิ่มเติม',
        confirmButtonText: 'ตกลง',
      })
      return
    }

    try {
      setApplyingId(card.id)
      const response = await applyPromotion(card.campaignId, 'campaign')
      await Swal.fire({
        icon: response.success ? 'success' : 'warning',
        title: response.success ? 'รับโบนัสสำเร็จ' : 'ไม่สามารถรับโบนัสได้',
        text: response.message || (response.success ? 'ระบบลงทะเบียนโบนัสให้คุณเรียบร้อยแล้ว' : 'กรุณาตรวจสอบเงื่อนไขอีกครั้ง'),
        confirmButtonText: 'ตกลง',
      })
    } catch (error) {
      if (String((error as Error)?.message || '') !== 'AUTH_EXPIRED') {
        await Swal.fire({
          icon: 'error',
          title: 'เกิดข้อผิดพลาด',
          text: error instanceof Error ? error.message : 'ไม่สามารถรับโบนัสได้',
          confirmButtonText: 'ตกลง',
        })
      }
    } finally {
      setApplyingId(null)
    }
  }

  const handleCardAction = async (card: PromotionCard) => {
    if (card.actionType === 'route' && card.actionValue) {
      navigate(card.actionValue)
      return
    }

    if (card.actionType === 'url' && card.actionValue) {
      if (card.openInNewTab) {
        window.open(card.actionValue, '_blank', 'noopener,noreferrer')
      } else {
        window.location.href = card.actionValue
      }
      return
    }

    if (card.actionType === 'none') {
      await Swal.fire({
        icon: 'info',
        title: card.title || 'โปรโมชั่น',
        text: card.description || 'กรุณาติดต่อแอดมินเพื่อดูรายละเอียดเพิ่มเติม',
        confirmButtonText: 'ตกลง',
      })
      return
    }

    await handleApply(card)
  }

  const featuredCards = useMemo(() => {
    const settings = hubConfig.settings as UserPromotionHubDisplaySettings
    return (settings.manualCards || [])
      .filter((card) => isManualCardActive(card))
      .sort((left, right) => left.sortOrder - right.sortOrder || left.title.localeCompare(right.title))
      .map((card): PromotionCard => ({
        id: card.id,
        title: card.title || 'โปรโมชั่น',
        description: card.description || 'โปรโมชั่นพิเศษสำหรับสมาชิก',
        buttonText: card.buttonText || 'ดูรายละเอียด',
        imageUrl: card.imageUrl || '',
        actionType: card.actionType,
        actionValue: card.actionValue,
        openInNewTab: card.openInNewTab,
        sortOrder: card.sortOrder,
      }))
  }, [hubConfig.settings])

  const shortcutCards = useMemo(() => {
    const settings = hubConfig.settings as UserPromotionHubDisplaySettings
    const cashbackSettings = cashbackConfig.settings as UserCashbackDisplaySettings
    const referralSettings = referralConfig.settings as UserReferralDisplaySettings
    const monthlyRewardSettings = monthlyRewardConfig.settings?.display as UserMonthlyRewardDisplaySettings
    const birthdaySettings = birthdayConfig.settings as UserManualReviewBonusDisplaySettings
    const facebookSettings = facebookShareConfig.settings as UserManualReviewBonusDisplaySettings

    const cards: PromotionCard[] = [
      {
        id: 'shortcut-cashback',
        title: cashbackSettings.promotionCard.title || settings.shortcuts.cashback.title,
        description: cashbackSettings.promotionCard.description || settings.shortcuts.cashback.description,
        buttonText: cashbackSettings.promotionCard.buttonText || settings.shortcuts.cashback.buttonText,
        imageUrl: cashbackSettings.promotionCard.imageUrl || '',
        actionType: 'route',
        actionValue: cashbackSettings.promotionRoute || settings.shortcuts.cashback.route || '/cashback',
        sortOrder: settings.shortcuts.cashback.sortOrder,
      },
      {
        id: 'shortcut-monthly-reward',
        title: monthlyRewardSettings?.promotionCard.title || settings.shortcuts.monthlyReward.title,
        description: monthlyRewardSettings?.promotionCard.description || settings.shortcuts.monthlyReward.description,
        buttonText: monthlyRewardSettings?.promotionCard.buttonText || settings.shortcuts.monthlyReward.buttonText,
        actionType: 'route',
        actionValue: monthlyRewardSettings?.promotionCard.route || settings.shortcuts.monthlyReward.route || '/reward',
        sortOrder: settings.shortcuts.monthlyReward.sortOrder,
      },
      {
        id: 'shortcut-referral',
        title: referralSettings.promotionCard.title || settings.shortcuts.referral.title,
        description: referralSettings.promotionCard.description || settings.shortcuts.referral.description,
        buttonText: referralSettings.promotionCard.buttonText || settings.shortcuts.referral.buttonText,
        imageUrl: referralSettings.promotionCard.imageUrl || '',
        actionType: 'route',
        actionValue: referralSettings.promotionRoute || settings.shortcuts.referral.route || '/affiliate',
        sortOrder: settings.shortcuts.referral.sortOrder,
      },
      {
        id: 'shortcut-birthday',
        title: birthdaySettings.promotionCard.title || settings.shortcuts.birthday.title,
        description: birthdaySettings.promotionCard.description || settings.shortcuts.birthday.description,
        buttonText: birthdaySettings.promotionCard.buttonText || settings.shortcuts.birthday.buttonText,
        imageUrl: birthdaySettings.promotionCard.imageUrl || '',
        actionType: 'route',
        actionValue: settings.shortcuts.birthday.route || '/contact?topic=birthday-bonus',
        sortOrder: settings.shortcuts.birthday.sortOrder,
      },
      {
        id: 'shortcut-facebook-share',
        title: facebookSettings.promotionCard.title || settings.shortcuts.facebookShare.title,
        description: facebookSettings.promotionCard.description || settings.shortcuts.facebookShare.description,
        buttonText: facebookSettings.promotionCard.buttonText || settings.shortcuts.facebookShare.buttonText,
        imageUrl: facebookSettings.promotionCard.imageUrl || '',
        actionType: 'route',
        actionValue: settings.shortcuts.facebookShare.route || '/contact?topic=facebook-share-bonus',
        sortOrder: settings.shortcuts.facebookShare.sortOrder,
      },
    ]

    return cards
      .filter((card) => {
        if (card.id === 'shortcut-cashback') return cashbackConfig.enabled
        if (card.id === 'shortcut-monthly-reward') return monthlyRewardConfig.enabled
        if (card.id === 'shortcut-referral') return referralConfig.enabled
        if (card.id === 'shortcut-birthday') return birthdayConfig.enabled
        if (card.id === 'shortcut-facebook-share') return facebookShareConfig.enabled
        return true
      })
      .sort((left, right) => (left.sortOrder || 0) - (right.sortOrder || 0) || left.title.localeCompare(right.title))
  }, [hubConfig.settings, cashbackConfig, referralConfig, birthdayConfig, facebookShareConfig, monthlyRewardConfig])

  const displayedCampaigns = useMemo(() => {
    const settings = hubConfig.settings as UserPromotionHubDisplaySettings
    const merged = [...shortcutCards, ...(settings.showCampaigns ? campaigns : [])]
    return merged
  }, [hubConfig.settings, shortcutCards, campaigns])

  const orderedSections = useMemo(() => {
    const settings = hubConfig.settings as UserPromotionHubDisplaySettings
    return [
      {
        id: 'warning',
        enabled: settings.sections.warning.enabled,
        sortOrder: settings.sections.warning.sortOrder,
      },
      {
        id: 'featured',
        enabled: settings.sections.featuredPromotions.enabled && featuredCards.length > 0,
        sortOrder: settings.sections.featuredPromotions.sortOrder,
      },
      {
        id: 'campaigns',
        enabled: settings.sections.campaigns.enabled || settings.sections.shortcuts.enabled,
        sortOrder: Math.min(settings.sections.campaigns.sortOrder, settings.sections.shortcuts.sortOrder),
      },
    ].filter((section) => section.enabled).sort((left, right) => left.sortOrder - right.sortOrder)
  }, [hubConfig.settings, featuredCards.length])

  const hubSettings = hubConfig.settings as UserPromotionHubDisplaySettings

  const renderPromotionCard = (card: PromotionCard) => (
    <div key={card.id} className="col-12 col-md-6 px-1 mb-3">
      <div
        className="bdr-5 p-2 p-md-3 h-100 d-flex flex-column overflow-hidden"
        style={{
          background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 246, 0.98))',
          border: '1px solid rgba(0, 148, 104, 0.18)',
          boxShadow: '0 8px 22px rgba(50, 39, 10, 0.12)',
        }}
      >
        <div
          className="position-relative overflow-hidden bdr-5 mb-3"
          style={{
            width: '100%',
            height: '156px',
            background: '#f4f6ef',
            border: '1px solid rgba(0, 148, 104, 0.12)',
          }}
        >
          <img
            src={resolveImage(card)}
            alt={card.title}
            style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
            onError={(event) => {
              event.currentTarget.src = DEFAULT_IMAGE
            }}
          />
        </div>

        <h5 className="t-medium mb-2" style={{ color: '#0c5132', lineHeight: 1.35, fontSize: '1.02rem' }}>
          {card.title}
        </h5>
        <p className="fs-14 mb-3 flex-grow-1" style={{ color: '#4f5b52', lineHeight: 1.65 }}>
          {card.description}
        </p>

        <button
          type="button"
          className="border-0 mt-auto d-flex align-items-center justify-content-center w-100 bdr-5"
          style={{
            background: 'linear-gradient(90deg, #6fcc2f 0%, #4fbf3c 50%, #2fbf8a 100%)',
            color: '#fff',
            fontWeight: 600,
            minHeight: '46px',
            height: '46px',
            padding: '0 16px',
            fontSize: '0.95rem',
            boxShadow: '0 8px 18px rgba(76, 191, 60, 0.20)',
          }}
          disabled={applyingId === card.id}
          onClick={() => void handleCardAction(card)}
        >
          {applyingId === card.id ? 'กำลังทำรายการ...' : (card.buttonText || hubSettings.receiveButtonText)}
        </button>
      </div>
    </div>
  )

  return (
    <div className="content pb-3">
      <div className="container">
        <div className="row px--1">
          <div className="col-12 px-1">
            <div className="bdr-5 bx-sd mb-2">
              <div className="row mx--1">
                <div className="col-12 px-1">
                  <div className="w-100 p-2 bgc-4 wrap justify-content-between">
                    <a
                      href="#"
                      className="txt-link-d"
                      onClick={(e) => {
                        e.preventDefault()
                        navigate('/dashboard')
                      }}
                    >
                      <i className="fas fa-chevron-left me-1"></i>
                      หน้าหลัก
                    </a>
                  </div>
                </div>

                <div className="col-12 px-1">
                  <div className="w-100 p-2 bgc-7">
                    <div className="bdr-5 bgc-4 c-red px-2 py-1 fs-18 t-medium mb-2">
                      <i className="fas fa-gift me-2"></i>
                      {hubSettings.pageTitle}
                    </div>

                    <div
                      className="bdr-5 p-3 p-md-4 mb-3 text-center overflow-hidden position-relative"
                      style={{
                        background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(246, 248, 242, 0.98))',
                        border: '1px solid rgba(0, 148, 104, 0.14)',
                        boxShadow: '0 8px 24px rgba(50, 39, 10, 0.10)',
                      }}
                    >
                      <small className="t-medium text-uppercase" style={{ color: '#009468', letterSpacing: '0.12em' }}>
                        {hubSettings.heroBadgeText}
                      </small>
                      <h4 className="mt-2 mb-2" style={{ color: '#0c5132' }}>{hubSettings.pageTitle}</h4>
                      <p className="fs-14 mb-0 mx-auto" style={{ color: '#5a655c', maxWidth: '680px' }}>
                        {hubSettings.pageSubtitle}
                      </p>
                    </div>

                    {!hubConfig.enabled ? (
                      <div
                        className="bdr-5 p-4 text-center"
                        style={{
                          background: 'rgba(5, 17, 38, 0.92)',
                          border: '1px solid rgba(61, 179, 255, 0.18)',
                        }}
                      >
                        <h5 className="t-medium mb-2" style={{ color: '#ff7b7b' }}>{hubSettings.disabledTitle}</h5>
                        <p className="mb-0" style={{ color: 'rgba(232, 242, 255, 0.8)' }}>{hubSettings.disabledMessage}</p>
                      </div>
                    ) : orderedSections.map((section) => {
                      if (section.id === 'warning') {
                        return (
                          <div
                            key={section.id}
                            className="bdr-5 p-3 mb-3"
                            style={{
                              background: 'linear-gradient(180deg, rgba(255, 249, 249, 0.98), rgba(255, 244, 247, 0.98))',
                              border: '1px solid rgba(219, 94, 94, 0.18)',
                            }}
                          >
                            <h6 className="t-medium mb-2" style={{ color: '#d65757' }}>{hubSettings.warningTitle}</h6>
                            <p className="fs-14 mb-1" style={{ color: '#6b5757' }}>{hubSettings.warningMessagePrimary}</p>
                            <p className="fs-14 mb-0" style={{ color: '#7a6666' }}>{hubSettings.warningMessageSecondary}</p>
                          </div>
                        )
                      }

                      if (section.id === 'featured') {
                        return (
                          <div
                            key={section.id}
                            className="bdr-5 p-2 p-md-3 mb-3"
                            style={{
                              background: 'rgba(255, 255, 255, 0.92)',
                              border: '1px solid rgba(0, 148, 104, 0.12)',
                            }}
                          >
                            <div className="wrap justify-content-between align-items-center mb-2">
                              <h6 className="t-medium mb-0" style={{ color: '#0c5132' }}>{hubSettings.featuredSectionTitle}</h6>
                              <small style={{ color: '#6c776f' }}>{hubSettings.featuredSectionSubtitle}</small>
                            </div>
                            <div className="row mx--1">
                              {featuredCards.map((card) => renderPromotionCard(card))}
                            </div>
                          </div>
                        )
                      }

                      return (
                        <div
                          key={section.id}
                          className="bdr-5 p-2 p-md-3 mb-2"
                          style={{
                            background: 'rgba(255, 255, 255, 0.92)',
                            border: '1px solid rgba(0, 148, 104, 0.12)',
                          }}
                        >
                          <div className="wrap justify-content-between align-items-center mb-2">
                            <h6 className="t-medium mb-0" style={{ color: '#0c5132' }}>{hubSettings.campaignsSectionTitle}</h6>
                            <small style={{ color: '#6c776f' }}>{hubSettings.campaignsSectionSubtitle}</small>
                          </div>

                          {loading ? (
                            <div
                              className="bdr-5 p-4 text-center"
                              style={{
                                background: 'rgba(8, 21, 48, 0.92)',
                                border: '1px solid rgba(61, 179, 255, 0.16)',
                              }}
                            >
                              <i className="fas fa-spinner fa-spin fs-24" style={{ color: '#79d5ff' }}></i>
                              <p className="mt-2 mb-0" style={{ color: 'rgba(232, 242, 255, 0.8)' }}>กำลังโหลดรายการโปรโมชั่น...</p>
                            </div>
                          ) : displayedCampaigns.length === 0 ? (
                            <div
                              className="bdr-5 p-4 text-center"
                              style={{
                                background: 'rgba(8, 21, 48, 0.92)',
                                border: '1px solid rgba(61, 179, 255, 0.16)',
                              }}
                            >
                              <i className="fas fa-gift fs-24" style={{ color: '#79d5ff' }}></i>
                              <p className="mt-2 mb-0" style={{ color: 'rgba(232, 242, 255, 0.8)' }}>{hubSettings.emptyCampaignText}</p>
                            </div>
                          ) : (
                            <div className="row mx--1">
                              {displayedCampaigns.map((card) => renderPromotionCard(card))}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PromotionPage
